import { FieldChange } from '@elementary/common-ui/types';
import { AddTestForm, SeverityFormField } from 'shared/types';

export type DirtyFields = Record<string, unknown>;

export type Mapper = {
  toForm: (planValue: unknown) => unknown;
  toPlan: (formValue: unknown) => unknown;
};

export type PlanFieldDescriptor = {
  planKey: string;
  targetPrefix?: 'params';
  formPath: string;
  mapper: Mapper;
};

export const extractNew = <T>(field?: FieldChange<T>): T | undefined =>
  field === undefined ? undefined : field.new;

export const setFieldNew = <T>(
  existing: FieldChange<T> | undefined,
  next: T,
): FieldChange<T> => ({
  current: existing?.current ?? (null as T),
  new: next,
});

const isNodeDirty = (node: unknown): boolean => {
  if (node === true) return true;
  if (Array.isArray(node)) return node.some(isNodeDirty);
  if (node && typeof node === 'object')
    return Object.values(node).some(isNodeDirty);
  return false;
};

export const isPathDirty = (
  dirtyFields: DirtyFields,
  path: string,
): boolean => {
  let node: unknown = dirtyFields;
  for (const segment of path.split('.')) {
    if (!node || typeof node !== 'object') return false;
    node = (node as Record<string, unknown>)[segment];
    if (node === undefined) return false;
  }
  return isNodeDirty(node);
};

const getPath = (obj: Record<string, unknown>, path: string): unknown =>
  path.split('.').reduce<unknown>((acc, key) => {
    if (acc && typeof acc === 'object')
      return (acc as Record<string, unknown>)[key];
    return undefined;
  }, obj);

const UNSAFE_PATH_SEGMENTS = ['__proto__', 'constructor', 'prototype'];

const setPath = (
  obj: Record<string, unknown>,
  path: string,
  value: unknown,
): void => {
  const segments = path.split('.');
  for (const segment of segments) {
    if (UNSAFE_PATH_SEGMENTS.includes(segment)) return;
  }
  let node = obj;
  for (let i = 0; i < segments.length - 1; i += 1) {
    const key = segments[i];
    if (UNSAFE_PATH_SEGMENTS.includes(key)) return;
    const child = node[key];
    if (!child || typeof child !== 'object') node[key] = {};
    node = node[key] as Record<string, unknown>;
  }
  const lastKey = segments[segments.length - 1];
  if (UNSAFE_PATH_SEGMENTS.includes(lastKey)) return;
  node[lastKey] = value;
};

const readPlanField = (
  change: Record<string, unknown>,
  descriptor: PlanFieldDescriptor,
): FieldChange<unknown> | undefined => {
  if (descriptor.targetPrefix === 'params') {
    const params = change.params as
      | Record<string, FieldChange<unknown>>
      | undefined;
    return params?.[descriptor.planKey];
  }
  return change[descriptor.planKey] as FieldChange<unknown> | undefined;
};

// When overlayOnly, skip descriptors absent from the change so a seeded base
// (e.g. the test's real current config) survives for fields the agent didn't touch.
export const changeToForm = (
  change: object,
  descriptors: PlanFieldDescriptor[],
  base: Partial<AddTestForm>,
  { overlayOnly = false }: { overlayOnly?: boolean } = {},
): Partial<AddTestForm> => {
  const source = change as Record<string, unknown>;
  const form = { ...base } as Record<string, unknown>;
  for (const descriptor of descriptors) {
    const planField = readPlanField(source, descriptor);
    if (overlayOnly && planField === undefined) continue;
    setPath(
      form,
      descriptor.formPath,
      descriptor.mapper.toForm(extractNew(planField)),
    );
  }
  return form as Partial<AddTestForm>;
};

export const formToChange = <TChange extends object>(
  change: TChange,
  form: Partial<AddTestForm>,
  dirtyFields: DirtyFields,
  descriptors: PlanFieldDescriptor[],
): TChange => {
  const source = change as Record<string, unknown>;
  let next: Record<string, unknown> = { ...source };
  for (const descriptor of descriptors) {
    if (!isPathDirty(dirtyFields, descriptor.formPath)) continue;
    const formValue = getPath(
      form as Record<string, unknown>,
      descriptor.formPath,
    );
    const planValue = descriptor.mapper.toPlan(formValue);

    if (descriptor.targetPrefix === 'params') {
      const params =
        (next.params as Record<string, FieldChange<unknown>> | undefined) ?? {};
      next = {
        ...next,
        params: {
          ...params,
          [descriptor.planKey]: setFieldNew(
            params[descriptor.planKey],
            planValue,
          ),
        },
      };
    } else {
      next = {
        ...next,
        [descriptor.planKey]: setFieldNew(
          source[descriptor.planKey] as FieldChange<unknown> | undefined,
          planValue,
        ),
      };
    }
  }
  return next as TChange;
};

export const planSeverityToForm = (
  severity?: string | null,
): SeverityFormField | undefined => {
  if (severity == null) return undefined;
  const normalized = severity.toLowerCase();
  if (normalized === 'error') return { type: 'error' };
  if (normalized === 'warning' || normalized === 'warn')
    return { type: 'warn' };
  return undefined;
};

// Severity round-trips through the form as a SeverityFormField; the plan stores
// it as a string whose casing differs between cloud (ERROR/WARNING) and code
// (error/warn).
export const makeSeverityMapper = (casing: 'upper' | 'lower'): Mapper => ({
  toForm: (planValue) =>
    planSeverityToForm(planValue as string | null) ?? { type: 'warn' },
  toPlan: (formValue) => {
    const severity = formValue as SeverityFormField | undefined;
    if (!severity?.type) return null;
    if (severity.type === 'error')
      return casing === 'upper' ? 'ERROR' : 'error';
    if (severity.type === 'warn')
      return casing === 'upper' ? 'WARNING' : 'warn';
    return null;
  },
});

export const listMapper: Mapper = {
  toForm: (planValue) => (Array.isArray(planValue) ? planValue : []),
  toPlan: (formValue) => (Array.isArray(formValue) ? formValue : []),
};

// Maps an optional scalar plan value straight to the form (null -> undefined so
// selects render empty) and back (undefined -> null so the plan clears it).
export const nullableMapper: Mapper = {
  toForm: (planValue) => planValue ?? undefined,
  toPlan: (formValue) => formValue ?? null,
};
